// pages/RMRDC/AdvancedAnalytics.js
import React, { useState, useEffect, useCallback, useRef, useMemo, useReducer } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Avatar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Slider,
  Checkbox,
  FormControlLabel,
  Switch,
  Divider,
  LinearProgress,
  Badge,
  useTheme,
  alpha,
  Stack,
  Fade,
  Grow,
  Zoom,
  TablePagination,
  InputAdornment,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Autocomplete,
  Collapse,
} from '@mui/material';
import {
  Download,
  Printer,
  FileSpreadsheet,
  FileText,
  TrendingUp,
  Users,
  Package,
  ShoppingCart,
  MapPin,
  BarChart3,
  PieChart,
  Filter,
  X,
  Plus,
  ChevronDown,
  ChevronUp,
  Star,
  Award,
  Zap,
  Clock,
  Calendar,
  Network,
  Globe,
  Activity,
  Target,
  GitBranch,
  Shield,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Table as TableIcon,
  Loader2,
  LayoutDashboard,
  Settings,
  Store,
  Search,
  RefreshCw,
  ChevronRight as ChevronRightIcon,
  MoreVert,
  TrendingDown,
  DollarSign,
  Truck,
  CheckCircle,
  AlertCircle,
  GitCompare,
  Layers,
  BarChart2,
  PieChart as PieChartIcon,
  ArrowLeft,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  LineChart,
  Line,
  ComposedChart,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ScatterChart,
  Scatter,
  ZAxis,
} from 'recharts';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { format, subMonths, isWithinInterval } from 'date-fns';
import { styled, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

// ============================================
// CONSTANTS
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const COLORS = [
  '#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b',
  '#ef4444', '#ec4899', '#14b8a6', '#3b82f6', '#8b5cf6',
  '#f472b6', '#34d399', '#fbbf24', '#a78bfa', '#60a5fa',
  '#f87171', '#6ee7b7', '#fcd34d', '#c084fc', '#93c5fd',
];

const COMPARISON_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

// ============================================
// STYLED COMPONENTS
// ============================================

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.05), 0 1px 2px -1px rgb(0 0 0 / 0.05)',
  border: '1px solid rgba(0,0,0,0.04)',
  transition: 'box-shadow 0.2s ease, transform 0.1s ease',
  height: '100%',
  '&:hover': {
    boxShadow: '0 4px 12px 0 rgb(0 0 0 / 0.08)',
  },
}));

const MetricCard = styled(Paper)(({ theme, color }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  border: '1px solid rgba(0,0,0,0.04)',
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '120px',
    height: '120px',
    background: `radial-gradient(circle, ${alpha(color || '#6366f1', 0.06)} 0%, transparent 70%)`,
    borderRadius: '50%',
    transform: 'translate(30%, -30%)',
  },
}));

const GradientIcon = styled(Box)(({ theme, color }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 48,
  height: 48,
  borderRadius: 12,
  background: `linear-gradient(135deg, ${alpha(color || '#6366f1', 0.12)} 0%, ${alpha(color || '#6366f1', 0.04)} 100%)`,
  color: color || '#6366f1',
  flexShrink: 0,
}));

const ComparisonCard = styled(Paper)(({ theme, isSelected }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  border: `2px solid ${isSelected ? theme.palette.primary.main : '#e5e7eb'}`,
  backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
}));

// ============================================
// COMPARISON REDUCER
// ============================================

const comparisonReducer = (state, action) => {
  switch (action.type) {
    case 'SET_TYPE':
      return { ...state, type: action.payload, items: [], data: null };
    case 'TOGGLE_ITEM':
      const maxItems = 5;
      const currentItems = state.items;
      if (currentItems.includes(action.payload)) {
        return { ...state, items: currentItems.filter(i => i !== action.payload) };
      } else if (currentItems.length < maxItems) {
        return { ...state, items: [...currentItems, action.payload] };
      }
      return state;
    case 'SET_ITEMS':
      return { ...state, items: action.payload };
    case 'SET_DATA':
      return { ...state, data: action.payload };
    case 'CLEAR':
      return { ...state, items: [], data: null };
    case 'TOGGLE_METRIC':
      return {
        ...state,
        metrics: { ...state.metrics, [action.payload]: !state.metrics[action.payload] }
      };
    case 'SET_MODAL_OPEN':
      return { ...state, modalOpen: action.payload };
    default:
      return state;
  }
};

// ============================================
// MAIN COMPONENT
// ============================================

export function RMRDCAnalytics() {
  const theme = useTheme();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState('chart');
  const [filters, setFilters] = useState({
    dateRange: {
      start: format(subMonths(new Date(), 3), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    },
    searchQuery: '',
    minRating: 0,
  });
  const [selectedMetric, setSelectedMetric] = useState('users');
  const [selectedGeoLevel, setSelectedGeoLevel] = useState('state');
  const [showFilters, setShowFilters] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [searchTerm, setSearchTerm] = useState('');

  // Comparison state using reducer
  const [comparison, dispatchComparison] = useReducer(comparisonReducer, {
    type: 'states',
    items: [],
    data: null,
    modalOpen: false,
    metrics: {
      showUsers: true,
      showRequests: true,
      showProducts: true,
      showGrowth: true,
      showRating: true,
      showStock: true,
    },
  });

  // ============================================
  // SNACKBAR
  // ============================================

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const hideSnackbar = useCallback(() => {
    setSnackbar({ open: false, message: '', severity: 'info' });
  }, []);

  // ============================================
  // DATA FETCHING
  // ============================================

  const getUserId = useCallback(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.id || 20;
      }
    } catch (e) {
      console.error('Error parsing user data:', e);
    }
    return 20;
  }, []);

  const fetchAnalyticsData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const userId = getUserId();
      const url = `${API_BASE_URL}/rmrdc/dashboard.php?user_id=${userId}`;

      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        setData(result.data);
        showSnackbar('Analytics data loaded successfully', 'success');
      } else {
        setError(result.message || 'Failed to load analytics data');
        showSnackbar(result.message || 'Failed to load analytics data', 'error');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Failed to connect to analytics server. Please check your connection.');
      showSnackbar('Failed to connect to analytics server', 'error');
    } finally {
      setLoading(false);
    }
  }, [getUserId, showSnackbar]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // ============================================
  // COMPARISON DATA PROCESSING
  // ============================================

  const getAvailableItems = useCallback((type) => {
    if (!data) return [];

    switch (type) {
      case 'states':
        return (data.geography?.users_by_state || [])
          .map(s => ({ id: s.state, label: s.state, value: s.state }))
          .filter(item => item.id);
      case 'products':
        return (data.analytics?.products || [])
          .map(p => ({ id: p.name, label: p.name, value: p.name, category: p.category }))
          .filter(item => item.id);
      case 'buyers':
        return (data.top_performers?.top_buyers || [])
          .map(b => ({ id: b.buyer_name, label: b.buyer_name, value: b.buyer_name, organisation: b.organisation }))
          .filter(item => item.id);
      default:
        return [];
    }
  }, [data]);

  const processComparisonData = useCallback((type, items) => {
    if (!data || items.length < 2) return null;

    let comparisonResult = {
      type,
      items: [],
      metrics: {},
      summary: {},
    };

    switch (type) {
      case 'states': {
        const statesData = data.geography?.users_by_state || [];
        const requestsData = data.geography?.orders_by_state || [];
        const productsData = data.geography?.products_by_state || [];

        items.forEach(stateName => {
          const stateUsers = statesData.find(s => s.state === stateName);
          const stateRequests = requestsData.find(s => s.state === stateName);
          const stateProducts = productsData.find(s => s.state === stateName);

          if (stateUsers) {
            comparisonResult.items.push({
              name: stateName,
              total_users: stateUsers.total || 0,
              buyers: stateUsers.buyers || 0,
              suppliers: stateUsers.suppliers || 0,
              active: stateUsers.active || 0,
              verified: stateUsers.verified || 0,
              active_rate: stateUsers.active_rate || 0,
              total_requests: stateRequests?.total_orders || 0,
              completed_requests: stateRequests?.delivered_orders || 0,
              total_products: stateProducts?.total_products || 0,
            });
          }
        });
        break;
      }

      case 'products': {
        const productsData = data.analytics?.products || [];
        const requestsData = data.analytics?.orders || [];

        items.forEach(productName => {
          const product = productsData.find(p => p.name === productName);
          if (product) {
            const productRequests = requestsData.filter(o => o.product_name === productName);
            comparisonResult.items.push({
              name: productName,
              stock: product.quantity_in_stock || 0,
              rating: product.average_rating || 0,
              category: product.category || '',
              status: product.status || '',
              total_requests: productRequests.length || product.total_orders || 0,
              views: product.views || 0,
              supplier: product.supplier_name || '',
            });
          }
        });
        break;
      }

      case 'buyers': {
        const buyersData = data.top_performers?.top_buyers || [];

        items.forEach(buyerName => {
          const buyer = buyersData.find(b => b.buyer_name === buyerName);
          if (buyer) {
            comparisonResult.items.push({
              name: buyerName,
              organisation: buyer.organisation || '',
              state: buyer.state || '',
              total_requests: buyer.total_orders || 0,
              join_date: buyer.join_date || '',
              last_request: buyer.last_order || '',
              preferred_category: buyer.preferred_category || '',
            });
          }
        });
        break;
      }

      default:
        return null;
    }

    // Calculate metrics
    if (comparisonResult.items.length > 0) {
      const numericKeys = Object.keys(comparisonResult.items[0])
        .filter(k => k !== 'name' && typeof comparisonResult.items[0][k] === 'number');

      numericKeys.forEach(key => {
        const values = comparisonResult.items.map(item => item[key] || 0);
        comparisonResult.metrics[key] = {
          min: Math.min(...values),
          max: Math.max(...values),
          avg: values.reduce((a, b) => a + b, 0) / values.length,
          total: values.reduce((a, b) => a + b, 0),
          difference: values.length >= 2 ? Math.abs(values[0] - values[values.length - 1]) : 0,
        };
      });

      // Summary statistics
      comparisonResult.summary = {
        mostValuable: numericKeys.reduce((acc, key) => {
          const maxItem = comparisonResult.items.reduce((a, b) => 
            (a[key] || 0) > (b[key] || 0) ? a : b
          );
          acc[key] = maxItem.name;
          return acc;
        }, {}),
        highestValue: numericKeys.reduce((acc, key) => {
          acc[key] = Math.max(...comparisonResult.items.map(i => i[key] || 0));
          return acc;
        }, {}),
        averageValues: numericKeys.reduce((acc, key) => {
          acc[key] = comparisonResult.metrics[key]?.avg || 0;
          return acc;
        }, {}),
      };
    }

    return comparisonResult;
  }, [data]);

  // ============================================
  // COMPARISON HANDLERS
  // ============================================

  const handleComparisonToggle = useCallback((item) => {
    dispatchComparison({ type: 'TOGGLE_ITEM', payload: item });
  }, []);

  const handleComparisonTypeChange = useCallback((type) => {
    dispatchComparison({ type: 'SET_TYPE', payload: type });
    dispatchComparison({ type: 'SET_ITEMS', payload: [] });
    dispatchComparison({ type: 'SET_DATA', payload: null });
  }, []);

  const handleApplyComparison = useCallback(() => {
    if (comparison.items.length < 2) {
      showSnackbar('Please select at least 2 items to compare', 'warning');
      return;
    }

    const result = processComparisonData(comparison.type, comparison.items);
    if (result) {
      dispatchComparison({ type: 'SET_DATA', payload: result });
      dispatchComparison({ type: 'SET_MODAL_OPEN', payload: false });
      showSnackbar(`Comparing ${comparison.items.length} ${comparison.type}`, 'success');
    } else {
      showSnackbar('Failed to process comparison data', 'error');
    }
  }, [comparison.items, comparison.type, processComparisonData, showSnackbar]);

  const handleClearComparison = useCallback(() => {
    dispatchComparison({ type: 'CLEAR' });
    showSnackbar('Comparison cleared', 'info');
  }, [showSnackbar]);

  const handleMetricToggle = useCallback((metric) => {
    dispatchComparison({ type: 'TOGGLE_METRIC', payload: metric });
  }, []);

  // ============================================
  // NAVIGATION
  // ============================================

  const handleBack = useCallback(() => {
    navigate('/rmrdc/Dashboard');
  }, [navigate]);

  const handleClose = useCallback(() => {
    window.close();
  }, []);

  // ============================================
  // EXPORT FUNCTIONS
  // ============================================

  const exportToCSV = useCallback(() => {
    const userId = getUserId();
    window.open(`${API_BASE_URL}/rmrdc/export_csv.php?user_id=${userId}`, '_blank');
    showSnackbar('CSV export initiated', 'success');
  }, [getUserId, showSnackbar]);

  const exportToExcel = useCallback(async () => {
    if (!data) return;
    setExportLoading(true);

    try {
      const exportData = {
        'User Statistics': data.stats?.users || {},
        'Product Statistics': data.stats?.products || {},
        'Request Statistics': data.stats?.orders || {},
        'Users by State': data.geography?.users_by_state || [],
        'Users by LGA': data.geography?.users_by_lga || [],
        'Products by State': data.geography?.products_by_state || [],
        'Requests by State': data.geography?.orders_by_state || [],
        'Category Distribution': data.categories?.distribution || [],
        'Top Suppliers': data.top_performers?.top_suppliers || [],
        'Top Chemical Users': data.top_performers?.top_buyers || [],
        'Monthly Trends': data.trends?.monthly_revenue || [],
        'Request Status': data.trends?.order_status || [],
      };

      const wb = XLSX.utils.book_new();
      Object.entries(exportData).forEach(([name, sheetData]) => {
        if (sheetData && (Array.isArray(sheetData) ? sheetData.length > 0 : Object.keys(sheetData).length > 0)) {
          const ws = XLSX.utils.json_to_sheet(Array.isArray(sheetData) ? sheetData : [sheetData]);
          XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
        }
      });

      XLSX.writeFile(wb, `RMRDC_Analytics_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      showSnackbar('Excel file downloaded successfully', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showSnackbar('Unable to generate Excel file', 'error');
    } finally {
      setExportLoading(false);
    }
  }, [data, showSnackbar]);

  const exportToPDF = useCallback(async () => {
    const element = document.getElementById('analytics-content');
    if (!element) {
      showSnackbar('Content not found', 'error');
      return;
    }

    setExportLoading(true);

    try {
      // Wait for charts to render
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: true,
        onclone: (clonedDoc) => {
          // Ensure all charts are visible
          const charts = clonedDoc.querySelectorAll('.recharts-wrapper');
          charts.forEach(chart => {
            chart.style.visibility = 'visible';
            chart.style.display = 'block';
          });
        }
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = pdfHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();

      while (heightLeft > 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }

      pdf.save(`RMRDC_Analytics_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
      showSnackbar('PDF downloaded successfully', 'success');
    } catch (error) {
      console.error('PDF export error:', error);
      showSnackbar('Unable to generate PDF. Please try again.', 'error');
    } finally {
      setExportLoading(false);
    }
  }, [showSnackbar]);

  const printReport = useCallback(() => {
    window.print();
  }, []);

  // ============================================
  // COMPARISON RENDERERS
  // ============================================

  const renderComparisonSelectionModal = useCallback(() => {
    const availableItems = getAvailableItems(comparison.type);
    const filteredItems = searchTerm
      ? availableItems.filter(item => 
          item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.organisation && item.organisation.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      : availableItems;

    return (
      <Dialog
        open={comparison.modalOpen}
        onClose={() => dispatchComparison({ type: 'SET_MODAL_OPEN', payload: false })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, maxHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e5e7eb', pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Compare {comparison.type.charAt(0).toUpperCase() + comparison.type.slice(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Select up to 5 items to compare
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                label={`${comparison.items.length}/5 selected`}
                color="primary"
                size="small"
                variant="outlined"
              />
              <IconButton onClick={() => dispatchComparison({ type: 'SET_MODAL_OPEN', payload: false })}>
                <X size={20} />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {comparison.items.map(item => (
              <Chip
                key={item}
                label={item}
                onDelete={() => handleComparisonToggle(item)}
                color="primary"
                size="small"
              />
            ))}
          </Box>

          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredItems.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No items found
              </Typography>
            ) : (
              filteredItems.map((item) => {
                const isSelected = comparison.items.includes(item.id);
                return (
                  <Paper
                    key={item.id}
                    sx={{
                      p: 2,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                      border: `1px solid ${isSelected ? theme.palette.primary.main : '#e5e7eb'}`,
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : alpha('#000', 0.02),
                      },
                    }}
                    onClick={() => handleComparisonToggle(item.id)}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
                        {item.label}
                      </Typography>
                      {item.organisation && (
                        <Typography variant="caption" color="text.secondary">
                          {item.organisation}
                        </Typography>
                      )}
                      {item.category && (
                        <Chip label={item.category} size="small" sx={{ ml: 1 }} />
                      )}
                    </Box>
                    <Checkbox checked={isSelected} />
                  </Paper>
                );
              })
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb' }}>
          <Button
            variant="outlined"
            onClick={() => {
              dispatchComparison({ type: 'SET_ITEMS', payload: [] });
              setSearchTerm('');
            }}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyComparison}
            disabled={comparison.items.length < 2}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Compare ({comparison.items.length})
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [comparison, getAvailableItems, handleComparisonToggle, handleApplyComparison, searchTerm, theme]);

  const renderComparisonResults = useCallback(() => {
    if (!comparison.data || comparison.data.items.length < 2) return null;

    const { items, metrics, type, summary } = comparison.data;
    const { metrics: metricToggles } = comparison;

    // Prepare chart data
    const chartData = items.map(item => ({
      ...item,
      name: item.name.length > 15 ? `${item.name.substring(0, 12)}...` : item.name,
      fullName: item.name,
    }));

    // Get chart metrics based on type
    const getChartMetrics = () => {
      switch (type) {
        case 'states':
          return [
            { key: 'total_users', label: 'Total Users', color: '#6366f1', show: metricToggles.showUsers },
            { key: 'buyers', label: 'Chemical Users', color: '#10b981', show: metricToggles.showUsers },
            { key: 'suppliers', label: 'Suppliers', color: '#f59e0b', show: metricToggles.showUsers },
            { key: 'total_requests', label: 'Requests', color: '#8b5cf6', show: metricToggles.showRequests },
          ];
        case 'products':
          return [
            { key: 'stock', label: 'Stock', color: '#f59e0b', show: metricToggles.showStock },
            { key: 'rating', label: 'Rating', color: '#10b981', show: metricToggles.showRating },
            { key: 'total_requests', label: 'Requests', color: '#8b5cf6', show: metricToggles.showRequests },
          ];
        case 'buyers':
          return [
            { key: 'total_requests', label: 'Requests', color: '#6366f1', show: metricToggles.showRequests },
          ];
        default:
          return [];
      }
    };

    const chartMetrics = getChartMetrics().filter(m => m.show);

    // Prepare radar chart data
    const radarData = chartMetrics.map(metric => ({
      subject: metric.label,
      ...items.reduce((acc, item, idx) => {
        acc[`value${idx}`] = item[metric.key] || 0;
        return acc;
      }, {}),
      fullMark: Math.max(...items.map(item => item[metric.key] || 0)) * 1.2 || 100,
    }));

    const radarColors = items.map((_, idx) => COMPARISON_COLORS[idx % COMPARISON_COLORS.length]);

    return (
      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {type.charAt(0).toUpperCase() + type.slice(1)} Comparison
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Comparing {items.length} items
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={type}
                  onChange={(e) => handleComparisonTypeChange(e.target.value)}
                >
                  <MenuItem value="states">States</MenuItem>
                  <MenuItem value="products">Products</MenuItem>
                  <MenuItem value="buyers">Chemical Users</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                startIcon={<X size={16} />}
                onClick={handleClearComparison}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Clear
              </Button>
            </Box>
          </Box>

          {/* Metric Toggles */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            {Object.keys(metricToggles).map(key => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={metricToggles[key]}
                    onChange={() => handleMetricToggle(key)}
                    size="small"
                  />
                }
                label={key.replace('show', '')}
                sx={{ mr: 1 }}
              />
            ))}
          </Box>

          {/* Main Comparison Chart */}
          {chartMetrics.length > 0 && (
            <Box sx={{ height: 400, mb: 4 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Key Metrics Comparison
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={70} 
                    interval={0} 
                    fontSize={11}
                  />
                  <YAxis fontSize={11} />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                      padding: '8px 12px',
                    }}
                    formatter={(value, name) => {
                      if (typeof value === 'number') {
                        return [value.toLocaleString(), name];
                      }
                      return [value, name];
                    }}
                  />
                  <Legend />
                  {chartMetrics.map((metric, idx) => (
                    <Bar
                      key={metric.key}
                      dataKey={metric.key}
                      fill={metric.color}
                      radius={[4, 4, 0, 0]}
                      name={metric.label}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* Radar Chart for Multi-Dimensional Comparison */}
          {items.length >= 2 && items.length <= 5 && radarData.length > 0 && (
            <Box sx={{ height: 350, mb: 4 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Multi-Dimensional Comparison
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#e5e7eb" />
                  <PolarAngleAxis dataKey="subject" fontSize={11} />
                  <PolarRadiusAxis fontSize={11} />
                  {items.map((item, idx) => (
                    <Radar
                      key={item.name}
                      name={item.name}
                      dataKey={`value${idx}`}
                      stroke={radarColors[idx % radarColors.length]}
                      fill={radarColors[idx % radarColors.length]}
                      fillOpacity={0.2}
                    />
                  ))}
                  <Legend />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                      padding: '8px 12px',
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* Comparison Table */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Detailed Comparison
            </Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Metric</TableCell>
                    {items.map((item, idx) => (
                      <TableCell key={idx} align="center" sx={{ fontWeight: 600 }}>
                        {item.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(metrics).map((metricKey) => {
                    const metric = metrics[metricKey];
                    return (
                      <TableRow key={metricKey}>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                          {metricKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </TableCell>
                        {items.map((item, idx) => {
                          const value = item[metricKey];
                          const isMax = value === metric.max && metric.max > metric.min;
                          const isMin = value === metric.min && metric.min < metric.max;
                          return (
                            <TableCell
                              key={idx}
                              align="center"
                              sx={{
                                color: isMax ? '#10b981' : isMin ? '#ef4444' : 'inherit',
                                fontWeight: isMax || isMin ? 600 : 400,
                                bgcolor: isMax ? alpha('#10b981', 0.06) : isMin ? alpha('#ef4444', 0.06) : 'transparent',
                              }}
                            >
                              {typeof value === 'number' 
                                ? value.toLocaleString()
                                : value || 'N/A'}
                              {isMax && <Star size={12} color="#f59e0b" style={{ marginLeft: 4 }} />}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Summary Insights */}
          {summary && (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Key Insights
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(summary.mostValuable).slice(0, 3).map(([key, value]) => (
                  <Grid item xs={12} sm={4} key={key}>
                    <Paper sx={{ p: 2, bgcolor: 'white' }} variant="outlined">
                      <Typography variant="caption" color="text.secondary">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Highest: {summary.highestValue[key]?.toLocaleString() || 0}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Paper>
      </Box>
    );
  }, [comparison, handleComparisonTypeChange, handleClearComparison, handleMetricToggle]);

  // ============================================
  // RENDER OVERVIEW
  // ============================================

  const renderOverview = useCallback(() => {
    if (!data) return null;

    const summaryCards = [
      {
        title: 'Total Users',
        value: data.summary?.total_users?.toLocaleString() || '0',
        icon: Users,
        color: '#6366f1',
        change: data.insights?.user_growth || 0,
        detail: `${data.stats?.users?.total_buyers || 0} Chemical Users • ${data.stats?.users?.total_suppliers || 0} Suppliers`,
      },
      {
        title: 'Total Requests',
        value: data.summary?.total_orders?.toLocaleString() || '0',
        icon: ShoppingCart,
        color: '#10b981',
        change: data.insights?.order_growth || 0,
        detail: `${data.stats?.orders?.delivered_orders || 0} Completed • ${data.stats?.orders?.pending_orders || 0} Pending`,
      },
      {
        title: 'Total Products',
        value: data.summary?.total_products?.toLocaleString() || '0',
        icon: Package,
        color: '#f59e0b',
        change: data.insights?.active_products_percentage || 0,
        detail: `${data.stats?.products?.active_products || 0} Active • ${data.stats?.products?.out_of_stock || 0} Out of Stock`,
      },
    ];

    return (
      <Box sx={{ mt: 3 }}>
        <Grid container spacing={3}>
          {summaryCards.map((card, idx) => (
            <Grid item xs={12} sm={6} lg={4} key={idx}>
              <MetricCard elevation={0} color={card.color}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, letterSpacing: '-0.02em' }}>
                      {card.value}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        label={`${card.change >= 0 ? '+' : ''}${card.change}%`}
                        size="small"
                        color={card.change >= 0 ? 'success' : 'error'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        vs last period
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {card.detail}
                    </Typography>
                  </Box>
                  <GradientIcon color={card.color}>
                    <card.icon size={24} />
                  </GradientIcon>
                </Box>
              </MetricCard>
            </Grid>
          ))}
        </Grid>

        {/* Comparison Button - Moved to Top */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<GitCompare size={18} />}
            onClick={() => dispatchComparison({ type: 'SET_MODAL_OPEN', payload: true })}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Compare {comparison.type.charAt(0).toUpperCase() + comparison.type.slice(1)}
          </Button>
          {comparison.items.length > 0 && (
            <Chip
              label={`${comparison.items.length} selected`}
              color="primary"
              onDelete={handleClearComparison}
            />
          )}
        </Box>

        {/* Comparison Results */}
        {renderComparisonResults()}

        {/* Charts - Using horizontal layout for better readability */}
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} lg={6}>
            <StyledCard>
              <CardHeader
                title="Users by State"
                action={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={selectedGeoLevel}
                        onChange={(e) => setSelectedGeoLevel(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="state">States</MenuItem>
                        <MenuItem value="lga">LGAs</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={selectedMetric}
                        onChange={(e) => setSelectedMetric(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="users">Users</MenuItem>
                        <MenuItem value="products">Products</MenuItem>
                        <MenuItem value="requests">Requests</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                }
              />
              <CardContent>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={(data.geography?.users_by_state || []).slice(0, 15)}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                      <XAxis type="number" fontSize={11} />
                      <YAxis 
                        dataKey="state" 
                        type="category" 
                        fontSize={10}
                        width={80}
                        tick={{ fontSize: 10 }}
                      />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                          padding: '8px 12px',
                        }}
                      />
                      <Legend />
                      <Bar 
                        dataKey="total" 
                        fill="#6366f1" 
                        radius={[0, 4, 4, 0]} 
                        name="Total Users"
                      />
                      <Bar 
                        dataKey="buyers" 
                        fill="#10b981" 
                        radius={[0, 4, 4, 0]} 
                        name="Chemical Users"
                      />
                      <Bar 
                        dataKey="suppliers" 
                        fill="#f59e0b" 
                        radius={[0, 4, 4, 0]} 
                        name="Suppliers"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <StyledCard>
              <CardHeader
                title="Requests by State"
                action={
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select
                      value={selectedMetric}
                      onChange={(e) => setSelectedMetric(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="users">Users</MenuItem>
                      <MenuItem value="products">Products</MenuItem>
                      <MenuItem value="requests">Requests</MenuItem>
                    </Select>
                  </FormControl>
                }
              />
              <CardContent>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={(data.geography?.orders_by_state || []).slice(0, 15)}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                      <XAxis type="number" fontSize={11} />
                      <YAxis 
                        dataKey="state" 
                        type="category" 
                        fontSize={10}
                        width={80}
                        tick={{ fontSize: 10 }}
                      />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                          padding: '8px 12px',
                        }}
                      />
                      <Legend />
                      <Bar 
                        dataKey="total_orders" 
                        fill="#8b5cf6" 
                        radius={[0, 4, 4, 0]} 
                        name="Total Requests"
                      />
                      <Bar 
                        dataKey="delivered_orders" 
                        fill="#10b981" 
                        radius={[0, 4, 4, 0]} 
                        name="Completed Requests"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Distribution Analysis */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} lg={6}>
            <StyledCard>
              <CardHeader title="Category Distribution" />
              <CardContent>
                <Box sx={{ height: 350 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={(data.categories?.distribution || []).slice(0, 8)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                        outerRadius={130}
                        dataKey="count"
                      >
                        {(data.categories?.distribution || []).slice(0, 8).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                          padding: '8px 12px',
                        }}
                      />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <StyledCard>
              <CardHeader title="Request Status Distribution" />
              <CardContent>
                <Box sx={{ height: 350 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={data.trends?.order_status || []}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                        outerRadius={130}
                        dataKey="value"
                      >
                        {(data.trends?.order_status || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                          padding: '8px 12px',
                        }}
                      />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Insights Section */}
        <Grid container spacing={3} sx={{ mt: 3 }}>
          <Grid item xs={12} md={4}>
            <StyledCard sx={{ borderLeft: `4px solid ${theme.palette.primary.main}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2 }}>
                    <TrendingUp size={24} color={theme.palette.primary.main} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Request Growth</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {data.insights?.order_growth || 0}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">vs previous period</Typography>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <StyledCard sx={{ borderLeft: `4px solid ${theme.palette.success.main}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
                    <Target size={24} color={theme.palette.success.main} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Conversion Rate</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {data.insights?.conversion_rate || 0}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">requests / users</Typography>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <StyledCard sx={{ borderLeft: `4px solid ${theme.palette.secondary.main}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.secondary.main, 0.1), borderRadius: 2 }}>
                    <Shield size={24} color={theme.palette.secondary.main} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Verification Rate</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {data.insights?.verification_rate || 0}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">verified / total users</Typography>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Insights Details */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <StyledCard>
              <CardHeader title="Key Insights" />
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    <Box sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 1 }}>
                      <Zap size={18} color={theme.palette.primary.main} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>User Growth</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {data.insights?.new_users_30d || 0} new users in the last 30 days
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    <Box sx={{ p: 1, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 1 }}>
                      <Activity size={18} color={theme.palette.success.main} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Active Users</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {data.insights?.active_user_rate || 0}% active rate across platform
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    <Box sx={{ p: 1, bgcolor: alpha(theme.palette.warning.main, 0.1), borderRadius: 1 }}>
                      <Truck size={18} color={theme.palette.warning.main} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Request Fulfillment</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {data.insights?.delivery_success_rate || 0}% request completion rate
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    <Box sx={{ p: 1, bgcolor: alpha(theme.palette.secondary.main, 0.1), borderRadius: 1 }}>
                      <Award size={18} color={theme.palette.secondary.main} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Supplier Verification</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {data.insights?.supplier_verification_rate || 0}% of suppliers verified
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      </Box>
    );
  }, [data, selectedMetric, selectedGeoLevel, theme, comparison, renderComparisonResults, handleClearComparison]);

  // ============================================
  // LOADING STATE
  // ============================================

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
          <CircularProgress size={48} sx={{ color: '#6366f1' }} />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Loading analytics data...
          </Typography>
          <Box sx={{ width: '100%', maxWidth: 400, mt: 3 }}>
            <LinearProgress />
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4 }}>
          <Box sx={{ mb: 2 }}>
            <AlertCircle size={48} color="#ef4444" />
          </Box>
          <Typography variant="h5" color="error" sx={{ fontWeight: 600 }}>
            Error Loading Data
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshCw size={18} />}
            onClick={fetchAnalyticsData}
            sx={{ mt: 3, borderRadius: 2, textTransform: 'none' }}
          >
            Retry
          </Button>
        </Paper>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4 }}>
          <Package size={48} color="#94a3b8" />
          <Typography variant="h5" sx={{ fontWeight: 600, mt: 2 }}>
            No Data Available
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            No analytics data found. Please try refreshing.
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshCw size={18} />}
            onClick={fetchAnalyticsData}
            sx={{ mt: 3, borderRadius: 2, textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Paper>
      </Container>
    );
  }

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <Box sx={{ bgcolor: '#f4f6fa', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">
        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={hideSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ borderRadius: 2, boxShadow: 3 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Header with Navigation and Export Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={handleBack}
              sx={{ 
                bgcolor: 'white', 
                boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                '&:hover': { bgcolor: '#f8fafc' }
              }}
            >
              <ArrowLeft size={20} />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                RMRDC Advanced Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Comprehensive market intelligence & insights
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Filter size={16} />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Filters
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileText size={16} />}
              onClick={exportToCSV}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              CSV
            </Button>
          
            <Button
              variant="outlined"
              size="small"
              startIcon={exportLoading ? <CircularProgress size={16} /> : <Download size={16} />}
              onClick={exportToPDF}
              disabled={exportLoading}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              PDF
            </Button>
         
          </Box>
        </Box>

        {/* Filters Panel */}
        {showFilters && (
          <Fade in={showFilters}>
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Date From"
                    type="date"
                    size="small"
                    fullWidth
                    value={filters.dateRange.start}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      dateRange: { ...prev.dateRange, start: e.target.value }
                    }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Date To"
                    type="date"
                    size="small"
                    fullWidth
                    value={filters.dateRange.end}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      dateRange: { ...prev.dateRange, end: e.target.value }
                    }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Search"
                    size="small"
                    fullWidth
                    placeholder="Search..."
                    value={filters.searchQuery}
                    onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={16} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshCw size={16} />}
                    onClick={() => setFilters(prev => ({
                      ...prev,
                      dateRange: {
                        start: format(subMonths(new Date(), 3), 'yyyy-MM-dd'),
                        end: format(new Date(), 'yyyy-MM-dd'),
                      },
                      searchQuery: '',
                      minRating: 0,
                    }))}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                    fullWidth
                  >
                    Reset Filters
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Box sx={{ minWidth: 200 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Min Rating: {filters.minRating}★
                      </Typography>
                      <Slider
                        value={filters.minRating}
                        onChange={(e, val) => setFilters(prev => ({ ...prev, minRating: val }))}
                        min={0}
                        max={5}
                        step={0.5}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Fade>
        )}

        {/* Main Content */}
        <Box id="analytics-content" ref={contentRef}>
          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Tabs
              value={activeTab}
              onChange={(e, val) => setActiveTab(val)}
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Overview" icon={<LayoutDashboard size={18} />} iconPosition="start" />
              <Tab label="Geography" icon={<MapPin size={18} />} iconPosition="start" />
              <Tab label="Products" icon={<Package size={18} />} iconPosition="start" />
              <Tab label="Requests" icon={<ShoppingCart size={18} />} iconPosition="start" />
              <Tab label="Chemical Users" icon={<Users size={18} />} iconPosition="start" />
              <Tab label="Insights" icon={<Target size={18} />} iconPosition="start" />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {activeTab === 0 && renderOverview()}
              {activeTab === 1 && <GeographyAnalytics data={data} comparison={comparison} />}
              {activeTab === 2 && <ProductAnalytics data={data} comparison={comparison} />}
              {activeTab === 3 && <RequestAnalytics data={data} comparison={comparison} />}
              {activeTab === 4 && <ChemicalUserAnalytics data={data} comparison={comparison} />}
              {activeTab === 5 && <InsightsAnalytics data={data} comparison={comparison} />}
            </Box>
          </Paper>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Generated {format(new Date(), 'MMM d, yyyy HH:mm:ss')} • RMRDC Analytics v4.0
          </Typography>
        </Box>

        {/* Comparison Modal */}
        {renderComparisonSelectionModal()}
      </Container>
    </Box>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function GeographyAnalytics({ data, comparison }) {
  const theme = useTheme();
  const usersByState = data?.geography?.users_by_state || [];
  const requestsByState = data?.geography?.orders_by_state || [];
  const selectedStates = comparison?.items || [];

  const filteredUsersByState = selectedStates.length > 0
    ? usersByState.filter(s => selectedStates.includes(s.state))
    : usersByState;

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <StyledCard>
            <CardHeader
              title="Users by State"
              action={
                selectedStates.length > 0 && (
                  <Chip
                    label={`Comparing ${selectedStates.length} states`}
                    color="primary"
                    size="small"
                  />
                )
              }
            />
            <CardContent>
              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={filteredUsersByState.slice(0, 15)}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                    <XAxis type="number" fontSize={11} />
                    <YAxis 
                      dataKey="state" 
                      type="category" 
                      fontSize={10}
                      width={80}
                      tick={{ fontSize: 10 }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                        padding: '8px 12px',
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="total" 
                      fill="#6366f1" 
                      radius={[0, 4, 4, 0]} 
                      name="Total Users"
                    />
                    <Bar 
                      dataKey="buyers" 
                      fill="#10b981" 
                      radius={[0, 4, 4, 0]} 
                      name="Chemical Users"
                    />
                    <Bar 
                      dataKey="suppliers" 
                      fill="#f59e0b" 
                      radius={[0, 4, 4, 0]} 
                      name="Suppliers"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <StyledCard>
            <CardHeader title="Requests by State" />
            <CardContent>
              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={requestsByState.slice(0, 15)}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                    <XAxis type="number" fontSize={11} />
                    <YAxis 
                      dataKey="state" 
                      type="category" 
                      fontSize={10}
                      width={80}
                      tick={{ fontSize: 10 }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                        padding: '8px 12px',
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="total_orders" 
                      fill="#8b5cf6" 
                      radius={[0, 4, 4, 0]} 
                      name="Total Requests"
                    />
                    <Bar 
                      dataKey="delivered_orders" 
                      fill="#10b981" 
                      radius={[0, 4, 4, 0]} 
                      name="Completed Requests"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12}>
          <StyledCard>
            <CardHeader title="Detailed Geographic Data" />
            <CardContent>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>State</TableCell>
                      <TableCell align="right">Total Users</TableCell>
                      <TableCell align="right">Chemical Users</TableCell>
                      <TableCell align="right">Suppliers</TableCell>
                      <TableCell align="right">Active</TableCell>
                      <TableCell align="right">Verified</TableCell>
                      <TableCell align="right">Active Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsersByState.map((state, idx) => (
                      <TableRow 
                        key={idx} 
                        hover
                        sx={{
                          bgcolor: selectedStates.includes(state.state) ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                        }}
                      >
                        <TableCell>
                          {state.state}
                          {selectedStates.includes(state.state) && (
                            <Chip label="Comparing" size="small" color="primary" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell align="right">{state.total}</TableCell>
                        <TableCell align="right">{state.buyers}</TableCell>
                        <TableCell align="right">{state.suppliers}</TableCell>
                        <TableCell align="right">{state.active}</TableCell>
                        <TableCell align="right">{state.verified}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${state.active_rate}%`}
                            size="small"
                            color={state.active_rate > 50 ? 'success' : 'warning'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
  );
}

function ProductAnalytics({ data, comparison }) {
  const theme = useTheme();
  const stats = data?.stats?.products || {};
  const distribution = data?.categories?.distribution || [];
  const products = data?.analytics?.products || [];
  const selectedProducts = comparison?.items || [];

  const filteredProducts = selectedProducts.length > 0
    ? products.filter(p => selectedProducts.includes(p.name))
    : products;

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <MetricCard elevation={0} color="#6366f1" sx={{ p: 3 }}>
            <Typography variant="caption" color="text.secondary">Total Products</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.total_products || 0}</Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard elevation={0} color="#10b981" sx={{ p: 3 }}>
            <Typography variant="caption" color="text.secondary">Active Products</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>{stats.active_products || 0}</Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard elevation={0} color="#f59e0b" sx={{ p: 3 }}>
            <Typography variant="caption" color="text.secondary">Avg Rating</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>{Number(stats.avg_rating || 0).toFixed(1)}★</Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <StyledCard>
            <CardHeader title="Category Distribution" />
            <CardContent>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={distribution.slice(0, 8)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={120}
                      dataKey="count"
                    >
                      {distribution.slice(0, 8).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                        padding: '8px 12px',
                      }}
                    />
                    <Legend />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <StyledCard>
            <CardHeader title="Product Status" />
            <CardContent>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { status: 'Active', count: stats.active_products || 0 },
                      { status: 'Inactive', count: stats.inactive_products || 0 },
                      { status: 'Out of Stock', count: stats.out_of_stock || 0 },
                      { status: 'Pending', count: stats.pending_products || 0 },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="status" fontSize={11} />
                    <YAxis fontSize={11} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                        padding: '8px 12px',
                      }}
                    />
                    <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12}>
          <StyledCard>
            <CardHeader 
              title="Top Products" 
              action={
                selectedProducts.length > 0 && (
                  <Chip
                    label={`Comparing ${selectedProducts.length} products`}
                    color="primary"
                    size="small"
                  />
                )
              }
            />
            <CardContent>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Stock</TableCell>
                      <TableCell align="right">Rating</TableCell>
                      <TableCell align="right">Requests</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredProducts.slice(0, 20).map((product, idx) => (
                      <TableRow 
                        key={idx} 
                        hover
                        sx={{
                          bgcolor: selectedProducts.includes(product.name) ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                        }}
                      >
                        <TableCell>
                          {product.name}
                          {selectedProducts.includes(product.name) && (
                            <Chip label="Comparing" size="small" color="primary" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell>{product.category}</TableCell>
                        <TableCell align="right">{product.quantity_in_stock}</TableCell>
                        <TableCell align="right">{Number(product.average_rating).toFixed(1)}★</TableCell>
                        <TableCell align="right">{product.total_orders || 0}</TableCell>
                        <TableCell>
                          <Chip label={product.status} size="small" color={product.status === 'active' ? 'success' : 'default'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
  );
}

function RequestAnalytics({ data, comparison }) {
  const theme = useTheme();
  const stats = data?.stats?.orders || {};
  const orderStatus = data?.trends?.order_status || [];
  const monthlyRevenue = data?.trends?.monthly_revenue || [];
  const orders = data?.analytics?.orders || [];

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={6} sm={3}>
          <MetricCard elevation={0} color="#6366f1" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Total Requests</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{stats.total_orders || 0}</Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard elevation={0} color="#10b981" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Completed</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981' }}>{stats.delivered_orders || 0}</Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard elevation={0} color="#f59e0b" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Pending</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#f59e0b' }}>{stats.pending_orders || 0}</Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard elevation={0} color="#8b5cf6" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Completion Rate</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {stats.total_orders > 0 ? Math.round((stats.delivered_orders / stats.total_orders) * 100) : 0}%
            </Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <StyledCard>
            <CardHeader title="Request Status Distribution" />
            <CardContent>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={orderStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={120}
                      dataKey="value"
                    >
                      {orderStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                        padding: '8px 12px',
                      }}
                    />
                    <Legend />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <StyledCard>
            <CardHeader title="Monthly Request Trend" />
            <CardContent>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyRevenue}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" fontSize={10} interval={1} />
                    <YAxis fontSize={11} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                        padding: '8px 12px',
                      }}
                    />
                    <Area type="monotone" dataKey="orders" stroke="#6366f1" fill="#6366f1" fillOpacity={0.2} name="Requests" />
                    <Area type="monotone" dataKey="completed_orders" stroke="#10b981" fill="#10b981" fillOpacity={0.2} name="Completed" />
                    <Legend />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12}>
          <StyledCard>
            <CardHeader title="Recent Requests" />
            <CardContent>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Reference</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.slice(0, 20).map((order, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{order.order_reference}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.status}
                            size="small"
                            color={
                              order.status === 'delivered' ? 'success' :
                              order.status === 'pending' ? 'warning' :
                              order.status === 'cancelled' ? 'error' :
                              'info'
                            }
                          />
                        </TableCell>
                        <TableCell>{format(new Date(order.order_date), 'MMM d, yyyy')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
  );
}

function ChemicalUserAnalytics({ data, comparison }) {
  const theme = useTheme();
  const stats = data?.stats?.users || {};
  const summary = data?.summary || {};
  const topBuyers = data?.top_performers?.top_buyers || [];
  const selectedBuyers = comparison?.items || [];

  const filteredBuyers = selectedBuyers.length > 0
    ? topBuyers.filter(b => selectedBuyers.includes(b.buyer_name))
    : topBuyers;

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={6} sm={3}>
          <MetricCard elevation={0} color="#6366f1" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Total Chemical Users</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{stats.total_buyers || 0}</Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard elevation={0} color="#10b981" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Active Chemical Users</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981' }}>{stats.active_buyers || 0}</Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard elevation={0} color="#8b5cf6" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Total Requests</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {stats.buyer_orders || 0}
            </Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard elevation={0} color="#f59e0b" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Active Rate</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {stats.total_buyers > 0 ? Math.round((stats.active_buyers / stats.total_buyers) * 100) : 0}%
            </Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <StyledCard>
            <CardHeader title="Chemical User Distribution" />
            <CardContent>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={[
                        { name: 'Active Chemical Users', value: stats.active_buyers || 0 },
                        { name: 'Inactive Chemical Users', value: (stats.total_buyers || 0) - (stats.active_buyers || 0) },
                      ]}
                      cx="50%"
                      cy="50%"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                      outerRadius={120}
                      dataKey="value"
                    >
                      <Cell fill="#10b981" />
                      <Cell fill="#94a3b8" />
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                        padding: '8px 12px',
                      }}
                    />
                    <Legend />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <StyledCard>
            <CardHeader title="Top Chemical Users by Activity" />
            <CardContent>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={filteredBuyers.slice(0, 10)}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                    <XAxis type="number" fontSize={11} />
                    <YAxis 
                      dataKey="buyer_name" 
                      type="category" 
                      fontSize={10}
                      width={100}
                      tick={{ fontSize: 10 }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                        padding: '8px 12px',
                      }}
                    />
                    <Bar dataKey="total_orders" fill="#6366f1" radius={[0, 4, 4, 0]} name="Total Requests" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12}>
          <StyledCard>
            <CardHeader 
              title="Top Chemical Users"
              action={
                selectedBuyers.length > 0 && (
                  <Chip
                    label={`Comparing ${selectedBuyers.length} chemical users`}
                    color="primary"
                    size="small"
                  />
                )
              }
            />
            <CardContent>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Chemical User</TableCell>
                      <TableCell>Organisation</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell align="right">Requests</TableCell>
                      <TableCell>Join Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredBuyers.slice(0, 20).map((buyer, idx) => (
                      <TableRow 
                        key={idx} 
                        hover
                        sx={{
                          bgcolor: selectedBuyers.includes(buyer.buyer_name) ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                        }}
                      >
                        <TableCell>
                          {buyer.buyer_name}
                          {selectedBuyers.includes(buyer.buyer_name) && (
                            <Chip label="Comparing" size="small" color="primary" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell>{buyer.organisation}</TableCell>
                        <TableCell>{buyer.state}</TableCell>
                        <TableCell align="right">{buyer.total_orders}</TableCell>
                        <TableCell>{buyer.join_date ? format(new Date(buyer.join_date), 'MMM d, yyyy') : 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
  );
}

function InsightsAnalytics({ data, comparison }) {
  const theme = useTheme();
  const insights = data?.insights || {};
  const weeklyTrends = data?.trends?.weekly_trends || [];

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <StyledCard sx={{ borderLeft: `4px solid ${theme.palette.primary.main}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2 }}>
                  <TrendingUp size={24} color={theme.palette.primary.main} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Request Growth</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{insights.order_growth || 0}%</Typography>
                  <Typography variant="caption" color="text.secondary">vs previous period</Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <StyledCard sx={{ borderLeft: `4px solid ${theme.palette.success.main}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
                  <Target size={24} color={theme.palette.success.main} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Conversion Rate</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{insights.conversion_rate || 0}%</Typography>
                  <Typography variant="caption" color="text.secondary">requests / users</Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <StyledCard sx={{ borderLeft: `4px solid ${theme.palette.secondary.main}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.secondary.main, 0.1), borderRadius: 2 }}>
                  <Shield size={24} color={theme.palette.secondary.main} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Verification Rate</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{insights.verification_rate || 0}%</Typography>
                  <Typography variant="caption" color="text.secondary">verified / total users</Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12}>
          <StyledCard>
            <CardHeader title="Key Insights" />
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Box sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 1 }}>
                    <Zap size={18} color={theme.palette.primary.main} />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>User Growth</Typography>
                    <Typography variant="caption" color="text.secondary">{insights.new_users_30d || 0} new users in the last 30 days</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Box sx={{ p: 1, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 1 }}>
                    <Activity size={18} color={theme.palette.success.main} />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Active Users</Typography>
                    <Typography variant="caption" color="text.secondary">{insights.active_user_rate || 0}% active rate across platform</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Box sx={{ p: 1, bgcolor: alpha(theme.palette.warning.main, 0.1), borderRadius: 1 }}>
                    <Truck size={18} color={theme.palette.warning.main} />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Request Fulfillment</Typography>
                    <Typography variant="caption" color="text.secondary">{insights.delivery_success_rate || 0}% request completion rate</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Box sx={{ p: 1, bgcolor: alpha(theme.palette.secondary.main, 0.1), borderRadius: 1 }}>
                    <Award size={18} color={theme.palette.secondary.main} />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Supplier Verification</Typography>
                    <Typography variant="caption" color="text.secondary">{insights.supplier_verification_rate || 0}% of suppliers verified</Typography>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12}>
          <StyledCard>
            <CardHeader title="Weekly Activity Pattern" avatar={<Calendar size={20} />} />
            <CardContent>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeklyTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" fontSize={11} />
                    <YAxis fontSize={11} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                        padding: '8px 12px',
                      }}
                    />
                    <Legend />
                    <Bar dataKey="total_orders" fill="#6366f1" radius={[4, 4, 0, 0]} name="Requests" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
  );
}

export default RMRDCAnalytics;